import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';

import ContextMenu from 'umg-ember-backend/components/ui/context-menu';
import Checkbox from 'umg-ember-backend/components/ui/form/field/checkbox';
import { HiddenIndicator } from 'umg-ember-backend/components/ui/hidden-indicator';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class PageBuilderPagesPreview extends Component {
  // Services
  @service intl;
  @service permission;

  // Getter and setter
  get isSelected() {
    return this.args.selectedPage == this.args.page;
  }

  // Functions
  toggleVisibility = () => {
    this.args.page.visible = !this.args.page.visible;
  };

  toggleStart = () => {
    let currentStartPage = this.args.page.mlp.pages.find((page) => page.start);

    if (currentStartPage) {
      currentStartPage.start = false;
      currentStartPage.setSlugAsLink();
    }

    this.args.page.start = !this.args.page.start;

    this.args.page.setSlugAsLink();
  };

  // Template
  <template>
    <div
      class={{bem
        (hash
          is-visible=@page.visible
          is-selected=this.isSelected
          has-error=(and @showErrors @page.validations.isInvalid)
        )
      }}
      {{sortableItem
        groupName=@sortableGroup
        disabled=@isDisabled
        model=@page
        onDragStart=@onDragStart
        onDragStop=@onDragEnd
      }}
    >
      <div
        class={{bem 'wrapper'}}
        role='button'
        {{on 'click' (fn @onSelectPage @page)}}
      >
        <div class={{bem 'handle'}} {{sortableHandle}}>
          {{svgJar 'drag' class=(bem 'drag-indicator')}}
        </div>

        <div class={{bem 'name'}}>
          {{#if @page.start}}
            {{svgJar 'home' class=(bem 'home-icon')}}
          {{/if}}
          {{@page.name}}
        </div>

        <HiddenIndicator @isVisible={{not @page.visible}} />

        <div class={{bem 'actions'}}>
          <ContextMenu
            @isDisabled={{@isDisabled}}
            @renderInPlace={{false}}
            @stopPropagation={{true}}
            as |menu|
          >
            <menu.item
              @icon='edit'
              @onClick={{fn @onEditPage @page}}
              @label={{t
                'page_builder.pages.preview.edit'
                displayLabel=@page.displayLabel
              }}
            />

            {{#if (can 'create' @page)}}
              <menu.item
                @icon='duplicate'
                @onClick={{fn @onDuplicatePage @page}}
                @label={{t
                  'page_builder.pages.preview.duplicate'
                  displayLabel=@page.displayLabel
                }}
              />
            {{/if}}

            {{#if (and (not @page.start) (can 'delete' @page))}}
              <menu.item
                @icon='trash'
                @onClick={{fn @onRemovePage @page}}
                @label={{t
                  'page_builder.pages.preview.delete'
                  displayLabel=@page.displayLabel
                }}
              />
            {{/if}}

            <hr />

            <menu.item
              @icon={{if @page.visible 'show' 'hide'}}
              @label={{t
                'page_builder.pages.preview.visibility'
                displayLabel=@page.displayLabel
              }}
            >
              <Checkbox
                @isChecked={{@page.visible}}
                @isDisabled={{cannot 'update' @page}}
                @recordType={{getRecordType @page}}
                @property='visible'
                @onChange={{this.toggleVisibility}}
              />
            </menu.item>

            {{#if this.permission.user.isAdmin}}
              <menu.item
                @icon='home'
                @label={{t
                  'page_builder.pages.preview.start'
                  displayLabel=@page.name
                }}
              >
                <Checkbox
                  @isChecked={{readonly @page.start}}
                  @recordType={{getRecordType @page}}
                  @property='start'
                  @isDisabled={{@page.start}}
                  @onChange={{this.toggleStart}}
                />
              </menu.item>
            {{/if}}
          </ContextMenu>
        </div>
      </div>
    </div>
  </template>
}
